import { CircularProgress } from '@material-ui/core'
import { createContext, PropsWithChildren, useContext, useMemo } from 'react'
import { useField } from 'react-final-form'
import { Options, useOptions } from '../../../../hooks/useOptions'
import * as types from '../../../../types'

// `treatmentType` is a single form field shared by every activity row. Each row
// used to subscribe to it separately, so a file with 79 activities registered
// 79 identical field subscriptions. react-final-form's per-field bookkeeping is
// what dominates the render cost of a large file (SLAAP-320), so it is
// subscribed once here and handed down instead.
type OptionsContextValue = Options & { treatmentType: types.TreatmentType }

const OptionsContext = createContext<OptionsContextValue | null>(null)

type OptionsContextProviderProps = PropsWithChildren<unknown>

export const OptionsContextProvider = ({
  children
}: OptionsContextProviderProps): JSX.Element => {
  const options = useOptions(false) // we are inside 2022 section so it's not legacy
  const {
    input: { value: treatmentType }
  } = useField('treatmentType')

  const value = useMemo(
    () => ({ ...options, treatmentType }),
    [options, treatmentType]
  )

  if (options.loading) return <CircularProgress />
  return (
    <OptionsContext.Provider value={value}>{children}</OptionsContext.Provider>
  )
}

export const useOptionsContext = (): OptionsContextValue => {
  const options = useContext(OptionsContext)
  if (!options) {
    throw new Error(
      'useOptionsContext must be used within OptionsContextProvider'
    )
  }
  return options
}
